import { ref, computed, onMounted } from 'vue';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa-install-dismissed';
const DISMISSED_EXPIRY_DAYS = 7;

/**
 * Composable for handling PWA installation prompts.
 * Supports both Android (beforeinstallprompt) and iOS (custom banner).
 */
export function usePwaInstall() {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const isInstalled = ref(false);
  const isDismissed = ref(false);
  const isIos = ref(false);
  const isAndroid = ref(false);
  const isSafari = ref(false);
  const isChrome = ref(false);
  const isStandalone = ref(false);

  /**
   * Check if the app is running in standalone mode (installed)
   */
  const checkStandalone = (): boolean => {
    if (typeof window === 'undefined') return false;

    // Check display-mode media query
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    if (standaloneQuery.matches) return true;

    // iOS Safari standalone mode
    if ('standalone' in window.navigator) {
      return (window.navigator as Navigator & { standalone: boolean }).standalone === true;
    }

    return false;
  };

  /**
   * Detect platform and browser
   */
  const detectPlatform = () => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();

    // iOS detection (iPhone, iPad, iPod)
    isIos.value = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Android detection
    isAndroid.value = /android/.test(ua);

    // Safari detection (not Chrome on iOS)
    isSafari.value = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);

    // Chrome detection
    isChrome.value = /chrome|crios/.test(ua) && !/edge|edg/.test(ua);

    // Standalone mode
    isStandalone.value = checkStandalone();
    isInstalled.value = isStandalone.value;
  };

  /**
   * Check if the dismiss was stored and still valid
   */
  const checkDismissed = () => {
    if (typeof window === 'undefined') return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const diffDays = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      if (diffDays < DISMISSED_EXPIRY_DAYS) {
        isDismissed.value = true;
      } else {
        localStorage.removeItem(DISMISSED_KEY);
      }
    }
  };

  /**
   * Whether to show the iOS install banner
   */
  const showIosBanner = computed(() => {
    return isIos.value && isSafari.value && !isStandalone.value && !isDismissed.value;
  });

  /**
   * Whether to show the Android install button (when beforeinstallprompt is available)
   */
  const showAndroidInstall = computed(() => {
    return deferredPrompt.value !== null && !isStandalone.value && !isDismissed.value;
  });

  /**
   * Whether to show any install prompt
   */
  const showInstallPrompt = computed(() => {
    return showIosBanner.value || showAndroidInstall.value;
  });

  /**
   * Dismiss the install prompt for a period
   */
  const dismiss = () => {
    isDismissed.value = true;
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
  };

  /**
   * Trigger the Android install prompt
   */
  const install = async (): Promise<boolean> => {
    if (!deferredPrompt.value) return false;

    try {
      await deferredPrompt.value.prompt();
      const { outcome } = await deferredPrompt.value.userChoice;

      if (outcome === 'accepted') {
        isInstalled.value = true;
        deferredPrompt.value = null;
        return true;
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }

    return false;
  };

  /**
   * Initialize the composable
   */
  const init = () => {
    detectPlatform();
    checkDismissed();

    // Listen for beforeinstallprompt (Android/Chrome)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      isInstalled.value = true;
      deferredPrompt.value = null;
    });
  };

  onMounted(() => {
    init();
  });

  return {
    // State
    isIos,
    isAndroid,
    isSafari,
    isChrome,
    isStandalone,
    isInstalled,
    isDismissed,

    // Computed
    showIosBanner,
    showAndroidInstall,
    showInstallPrompt,

    // Actions
    dismiss,
    install,
  };
}
